import {Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ProjectFile} from '../../../api';
import {ProjectFileTableService} from '../../../services/files/project-file-table.service';
import {StateConfigService} from '../../../services/state/state-config.service';
import {PopoutContentService} from '../../../services/popout/popout.service';
import {ActivatedRoute} from '@angular/router';
import {faArrowUpRightFromSquare, faLayerGroup} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {finalize} from 'rxjs';

import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {BicButtonComponent, TooltipDirective} from '@buildinginformationcloud/bic-ui-components';
import {FilesComponent} from '../../files/files.component';
import {PageHeaderComponent} from '../design/page-header/page-header.component';

@Component({
  selector: 'app-ifc-viewer',
  templateUrl: './ifc-viewer.component.html',
  styleUrls: ['./ifc-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FaIconComponent,
    BicButtonComponent,
    FilesComponent,
    TooltipDirective,
    PageHeaderComponent,
  ]
})
export class IfcViewerComponent implements OnInit {
  selectedFiles: ProjectFile[] = [];
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected stateConfigService = inject(StateConfigService);
  private fileTableService = inject(ProjectFileTableService);

  ifcFiles: ProjectFile[] = [];
  protected loading = true;
  protected readonly faLayerGroup = faLayerGroup;
  protected readonly faSpinnerThird = faSpinnerThird;
  private activatedRoute = inject(ActivatedRoute);
  private popoutService = inject(PopoutContentService);

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['url']) {
        this.popoutService.opened = true;
      }
    });

    // Die schlanke Tabellenprojektion reicht hier: gebraucht werden fileType, id, parentId und die
    // Felder, die <app-files> anzeigt. Weder `link` noch `fragmentsLink` werden ausgewertet — die
    // beiden Buttons öffnen nur die Route /projects/{id}/files/{fileId}, die die Datei selbst nachlädt.
    // Bewusst OHNE fileType-Filter: die Elternordner werden unten per id nachgeschlagen, dafür müssen
    // alle Einträge des Projekts vorliegen.
    this.fileTableService.getTableRows(this.stateConfigService.getProjectId()).pipe(
      finalize(() => this.loading = false)
    ).subscribe(files => {
      const ifcAndCoordinationFiles = files.filter(file =>
        file.fileType === ProjectFile.FileTypeEnum.IFC ||
        file.fileType === ProjectFile.FileTypeEnum.COORDINATION
      );

      // Include the complete parent folder hierarchy (not just the direct
      // parent) so that files nested deeper than one level stay reachable in
      // the folder navigation.
      ifcAndCoordinationFiles.forEach(file => {
        let parentId = file.parentId;
        const visitedIds = new Set<string>();
        while (parentId) {
          if (visitedIds.has(parentId)) {
            break;
          }
          visitedIds.add(parentId);

          const folder = files.find(f => f.id === parentId);
          if (!folder) {
            break;
          }
          if (!this.ifcFiles.some(f => f.id === folder.id)) {
            this.ifcFiles.push(folder);
          }
          parentId = folder.parentId;
        }
      });

      this.ifcFiles = [...this.ifcFiles, ...ifcAndCoordinationFiles];
    });
  }

  openIFCViewer(): void {
    const file = this.getSelectedIfcFiles()[0];
    if (!file) return;
    const baseUrl = window.location.origin + '/projects/' + this.stateConfigService.getProjectId() + '/files/' + file.id;
    this.popoutService.open(baseUrl, file.name ?? 'IFC-Viewer');
  }

  getSelectedIfcFiles(): ProjectFile[] {
    return this.selectedFiles.filter(f =>
      !f.isFolder && (f.fileType === ProjectFile.FileTypeEnum.IFC || f.fileType === ProjectFile.FileTypeEnum.COORDINATION)
    );
  }

  openCoordinationModel(): void {
    const ifcFiles = this.getSelectedIfcFiles();
    if (ifcFiles.length < 2) return;

    const primaryFile = ifcFiles[0];
    const additionalIds = ifcFiles.slice(1).map(f => f.id).join(',');
    const baseUrl = window.location.origin + '/projects/' + this.stateConfigService.getProjectId() + '/files/' + primaryFile.id
      + '?coordination=' + additionalIds;
    this.popoutService.open(baseUrl, 'Koordinationsmodell');
  }
}
